import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { build, createItem, newTrack } from '@easyedit/timeline'
import { api } from '../../../lib/api/client'
import type { Project } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'

interface BrandKit {
  id: string
  name: string
  colors: string[]
  fonts: Array<{ family: string; role?: string }>
  isDefault: boolean
}

/** Cores de saída quando o workspace ainda não tem Brand Kit. */
const FALLBACK = { primary: '#FFFFFF', secondary: '#E8E8E8', accent: '#E8A33D', font: 'Inter' }

export function TextPanel({ project }: { project: Project }) {
  const sequence = useEditorStore((s) => s.sequence)
  const dispatch = useEditorStore((s) => s.dispatch)
  const playhead = useEditorStore((s) => s.playhead)
  const [content, setContent] = useState('Seu texto aqui')

  const kits = useQuery({
    queryKey: ['brandkits', project.workspaceId],
    queryFn: () => api.get<BrandKit[]>(`/workspaces/${project.workspaceId}/brand-kits`),
    staleTime: 60_000,
  })

  /**
   * O kit do projeto, ou o padrão do workspace.
   *
   * Enquanto as cores viviam cravadas neste arquivo, uma agência com dez clientes
   * recebia o mesmo laranja em todos — e a identidade que o Brand Kit guarda não
   * chegava ao único lugar onde o usuário cria texto à mão.
   */
  const brand = useMemo(() => {
    const list = kits.data ?? []
    const kit = list.find((k) => k.id === project.brandKitId) ?? list.find((k) => k.isDefault)
    if (!kit) return FALLBACK
    return {
      primary: kit.colors[0] ?? FALLBACK.primary,
      secondary: kit.colors[1] ?? kit.colors[0] ?? FALLBACK.secondary,
      accent: kit.colors[2] ?? kit.colors[1] ?? FALLBACK.accent,
      font: kit.fonts[0]?.family ?? FALLBACK.font,
    }
  }, [kits.data, project.brandKitId])

  const presets = useMemo(
    () => [
      { label: 'Título', fontSize: 96, fontWeight: 800, color: brand.primary },
      { label: 'Subtítulo', fontSize: 56, fontWeight: 600, color: brand.secondary },
      { label: 'Legenda manual', fontSize: 44, fontWeight: 600, color: brand.primary },
      { label: 'Destaque', fontSize: 72, fontWeight: 800, color: brand.accent },
    ],
    [brand],
  )

  function addText(preset: (typeof presets)[number]) {
    if (!sequence) return
    const ctx = { sequenceId: sequence.id }
    const item = createItem({ kind: 'text', start: playhead, duration: 4, label: content.slice(0, 30) })
    item.text = {
      ...item.text!,
      content,
      fontFamily: brand.font,
      fontSize: preset.fontSize,
      fontWeight: preset.fontWeight,
      color: preset.color,
      stroke: { color: '#000000', width: 4 },
      shadow: { color: '#00000099', x: 0, y: 4, blur: 12 },
    }
    item.fadeIn = 0.25
    item.fadeOut = 0.25

    // Sem track de texto livre, cria uma — no MESMO comando da inserção, para o undo
    // desfazer "coloquei um título" e não duas coisas.
    const track = sequence.tracks.find((t) => t.kind === 'text' && !t.locked)
    if (track) {
      void dispatch(build.addText(ctx, track.id, item))
      return
    }
    const created = newTrack(sequence.tracks, 'text')
    void dispatch(
      build.batch(ctx, [build.addTrack(ctx, created), build.addText(ctx, created.id, item)]),
    )
  }

  return (
    <div className="p-3 flex flex-col gap-3">
      <h2 className="text-xs font-semibold">Texto</h2>
      <label className="flex flex-col gap-1">
        <span className="label">Conteúdo</span>
        <textarea
          id="text-content"
          className="field min-h-[64px] resize-y"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>
      <div className="grid gap-1.5">
        {presets.map((preset) => (
          <button
            key={preset.label}
            className="btn-outline justify-between py-2"
            onClick={() => addText(preset)}
            disabled={!sequence}
          >
            <span style={{ color: preset.color, fontWeight: preset.fontWeight }}>{preset.label}</span>
            <span className="font-mono text-2xs text-ink-500">{preset.fontSize}px</span>
          </button>
        ))}
      </div>
      <p className="text-2xs text-ink-500 leading-relaxed">
        {kits.data && kits.data.length > 0
          ? `Cores e fonte vêm do Brand Kit (${brand.font}).`
          : 'Sem Brand Kit no workspace — usando as cores padrão.'}{' '}
        O texto entra no playhead com 4 segundos; ajuste tudo no painel de propriedades.
      </p>
    </div>
  )
}
